import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { PersonalService } from 'src/app/core/services/modules/personal.service';
import { DialogService } from 'src/app/core/services/utils/dialog.service';
import { ExtractedDocumentResponse } from 'src/app/core/interfaces/interfaces';
import { AuthGuardService } from 'src/app/core/services/guards/AuthGuardService';

export interface DialogChooseEnrollmentMethodData {
  documentType?: string;
  documentNumber?: string;
  proyectoId?: string;
  empresaId?: string;
  documentTypeLabel?: string;
}

export type ChooseEnrollmentMethodResult =
  | { mode: 'cancel' }
  | { mode: 'manual' }
  | { mode: 'ai'; extracted: ExtractedDocumentResponse; useExtractedNumber?: boolean };

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf'];

@Component({
  selector: 'app-dialog-choose-enrollment-method',
  templateUrl: './dialog-choose-enrollment-method.component.html',
  styleUrls: ['./dialog-choose-enrollment-method.component.scss']
})
export class DialogChooseEnrollmentMethodComponent {
  step: 'choice' | 'uploading' | 'notice' = 'choice';
  noticeMessage = '';
  permissionsCompany: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogChooseEnrollmentMethodComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogChooseEnrollmentMethodData,
    private personalService: PersonalService,
    private dialogService: DialogService,
    private authService: AuthGuardService
  ) {
    this.permissionsCompany = this.authService.auth.getUser().Companies.find(x => x.Id === this.data.empresaId)?.Permissions || [];
  }

  get canUploadDocument(): boolean {
    return this.exitPermissionCompany('personals.Uploaddocument');
  }

  get canManualRegistration(): boolean {
    return this.exitPermissionCompany('personals.Manualregistration');
  }

  exitPermissionCompany(permiso: string): boolean {
    return this.permissionsCompany.find(x => x === permiso) !== undefined;
  }

  onSelect(event: any): void {
    const selectedFiles: File[] = event?.addedFiles;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }
    const file = selectedFiles[0];
    const extension = (file.name.split('.').pop() || '').toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      this.showNotice('Formato no soportado. Usa PNG, JPG o PDF.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.showNotice('El archivo supera el tamaño máximo permitido (10MB).');
      return;
    }

    this.extractDocument(file);
  }

  private extractDocument(file: File): void {
    this.step = 'uploading';
    this.personalService.extractPersonalDocument(file, this.data?.proyectoId, this.data?.empresaId).subscribe(
      (extracted: ExtractedDocumentResponse) => {
        if (extracted?.esDocumentoIdentidad === false) {
          this.showNotice('El archivo no parece un documento de identidad. Verifíquelo o diligencie manualmente.');
          return;
        }
        this.handleExtractedSuccess(extracted);
      },
      (error: HttpErrorResponse | any) => {
        this.logFailedExtractionRequestId(error);

        if (error?.name === 'TimeoutError') {
          this.showNotice('No fue posible leer el documento. Diligencie los datos manualmente.');
          return;
        }
        switch (error?.status) {
          case 413:
            this.showNotice('El archivo supera el tamaño máximo permitido (10MB).');
            break;
          case 415:
            this.showNotice('Formato de archivo no soportado. Usa PNG, JPG o PDF.');
            break;
          case 422:
            this.showNotice('El archivo no parece un documento de identidad. Verifíquelo o diligencie manualmente.');
            break;
          default:
            // 502 / 504 / red / timeout → degradación elegante: no bloquear el enrolamiento
            this.showNotice('No fue posible leer el documento. Diligencie los datos manualmente.');
        }
      }
    );
  }

  private logFailedExtractionRequestId(error: HttpErrorResponse | any): void {
    const requestId = error?.error?.requestId
      || error?.headers?.get?.('X-Request-Id')
      || error?.headers?.get?.('x-request-id')
      || null;
    console.warn('Extracción fallida. RequestId:', requestId);
  }

  private handleExtractedSuccess(extracted: ExtractedDocumentResponse): void {
    const digitedNumber = this.normalizeDocNumber(this.data.documentNumber);
    const extractedNumber = this.normalizeDocNumber(extracted.numero);

    if (digitedNumber && extractedNumber && digitedNumber !== extractedNumber) {
      this.dialogService.openDialogConfirm(
        `El número extraído del documento (${this.formatDocNumber(extracted.numero)}) no coincide con el número digitado ` +
        `(${this.formatDocNumber(this.data.documentNumber)}). ¿Desea continuar de todos modos con el número extraído?`
      ).afterClosed().subscribe((confirmed: any) => {
        if (confirmed) {
          this.dialogRef.close({ mode: 'ai', extracted, useExtractedNumber: true } as ChooseEnrollmentMethodResult);
        } else {
          this.step = 'choice';
        }
      });
      return;
    }

    this.dialogRef.close({ mode: 'ai', extracted } as ChooseEnrollmentMethodResult);
  }

  private normalizeDocNumber(value: string | null | undefined): string {
    return (value || '').toString().replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  }

  private formatDocNumber(value: string | null | undefined): string {
    return (value || '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  private showNotice(message: string): void {
    this.noticeMessage = message;
    this.step = 'notice';
  }

  chooseManual(): void {
    this.dialogRef.close({ mode: 'manual' } as ChooseEnrollmentMethodResult);
  }

  cancel(): void {
    this.dialogRef.close({ mode: 'cancel' } as ChooseEnrollmentMethodResult);
  }
}
